import React, { useEffect, useState } from "react";
import { InputCompo } from "../../../Components/InputCompo/InputCompo";
import CustomInput from "../../../Components/InputCompo/CustomInput";
import { ParagraphComp } from "../../../Components/ParagraphComp/ParagraphComp";
import ButtonComp from "../../../Components/ButtonComp/ButtonComp";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  undertakingDocuments as documents,
  undertakingCheckbox,
} from "./Documents";
import { useMutation, useQuery } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import {
  ContributionGet,
  ContributionRegistration,
  EOIGet,
} from "../../../Store/auth/register";

const UndertakingInformation = ({ setSubTabs }) => {
  const [initialValues, setInitialValues] = useState({});

  const gsnNoFromStorage = localStorage.getItem("gsn_no");

  const { data: EoiQuestions } = useQuery({
    queryKey: "EOI",
    queryFn: () => EOIGet(gsnNoFromStorage ? gsnNoFromStorage : ""),
  });

  if (!EoiQuestions) {
    setSubTabs({ "Organization Details": true });
  }

  const { data: questions } = useQuery({
    queryKey: "Contribution",
    queryFn: () => ContributionGet(gsnNoFromStorage ? gsnNoFromStorage : ""),
  });

  const modelSchema = Yup.object().shape(
    documents.reduce((schema, field) => {
      if (field.mandatory === 1 && field.type.toLowerCase() !== "section") {
        let validator = Yup.string().required(`${field.question} is required`);

        if (field.validation) {
          switch (field.validation.toLowerCase()) {
            case "email":
              validator = Yup.string()
                .email("Invalid email address")
                .required(`${field.question} is required`);
              break;
            case "number":
              validator = Yup.string()
                .matches(/^\d{10}$/, "Invalid Number. It should be 10 digits.")
                .required(`${field.question} is required`);
              break;
            case "pan":
              validator = Yup.string()
                .matches(
                  /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/,
                  "Invalid PAN Number. It should be in the format: ABCDE1234F."
                )
                .required(`${field.question} is required`);
              break;
            case "pincode":
              validator = Yup.string()
                .matches(/^\d{4}$|^\d{6}$/, "PIN code must be exactly 6 digits")
                .required(`${field.question} is required`);
              break;
            case "areacode":
              validator = Yup.string()
                .matches(/^(123|456|789)$/, "Incorrect area code")
                .required(`${field.question} is required`);
              break;
            case "website":
              validator = Yup.string()
                .url("Please enter a valid URL")
                .required(`${field.question} is required`);
              break;
            case "mobile":
              validator = Yup.string()
                .matches(/^(?:[0-9]{10}|[0-9]{7})$/, "Invalid contact number")
                .required(`${field.question} is required`);
              break;
            case "darpan":
              validator = Yup.string()
                .test(
                  "is-valid-darpan",
                  "Darpan Id should be in the format: XX/1234/1234567",
                  (value) =>
                    value?.toUpperCase() === "NA" ||
                    /^([A-Z]{2}\/\d{4}\/\d{7})$/.test(value)
                )
                .required(`${field.question} is required`);
              break;
            case "password":
              validator = Yup.string()
                .min(8, "Password must be at least 8 characters long")
                .required(`${field.question} is required`);
              break;
            case "multiselect dropdown":
              validator = Yup.array().required(`${field.question} is required`);
              break;
            case "attach":
              validator = Yup.object().required(
                `${field.question} is required`
              );
              break;
            default:
              validator = Yup.string().required(
                `${field.question} is required`
              );
              break;
          }
        }

        schema[field.question_no] = validator;
      }

      return schema;
    }, {})
  );

  useEffect(() => {
    // Check if API response is available
    const apiData = questions?.documents || {};
    const savedData =
      JSON.parse(localStorage.getItem("ContributionData1")) || {};

    // Initialize form values with default data if API data is not available
    const formValues = {
      // Reduce over `documents` to populate initial values
      ...documents.reduce((acc, field) => {
        acc[field.question_no] =
          apiData[field.question_no] || savedData[field.question_no] || ""; // Default to empty string if no data
        return acc;
      }, {}),

      // Reduce over `undertakingCheckbox` to populate initial values
      ...undertakingCheckbox.reduce((acc, checkbox) => {
        if (checkbox.isCustomInput) {
          // Handle custom input fields for checkboxes
          acc[checkbox.name] =
            apiData[checkbox.name] || savedData[checkbox.name] || "";
        } else {
          // For regular checkboxes, default to `false` if no data is available
          acc[checkbox.name] =
            apiData[checkbox.name] || savedData[checkbox.name] || false;
        }
        return acc;
      }, {}),
    };

    // Set form values (either from API, localStorage, or default)
    setInitialValues(formValues);
  }, [questions]);

  const formik = useFormik({
    initialValues: initialValues,

    validationSchema: modelSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        // Convert the form data into a flat object
        const tempData = {
          ...documents.reduce((acc, field) => {
            acc[field.question_no] = values[field.question_no];
            return acc;
          }, {}),
          ...undertakingCheckbox.reduce((acc, checkbox) => {
            if (checkbox.isCustomInput) {
              // If the checkbox has an associated input field, save the input field's value
              acc[checkbox.name] = values[checkbox.name];
            } else {
              // For normal checkboxes, save "Yes" or "No"
              acc[checkbox.name] = values[checkbox.name] ? 1 : 0;
            }
            return acc;
          }, {}),
        };

        // Merge new data with existing data
        const updatedData = {
          ...tempData,
          guidestar_number: gsnNoFromStorage,
        };
        const structuredData = {
          args: updatedData,
        };

        
        // Optionally, navigate to the next page or perform another action
        await submitReg(structuredData);
      } catch (error) {
        console.error(error);
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleCreateSuccess = () => {
    enqueueSnackbar("success!", { variant: "success" });
    // localStorage.clear('ContributionData')
  };

  const handleCreateError = (error) => {
    if (error?.response?.data.status_code >= 500) {
      enqueueSnackbar(error?.response?.data?.status_message, {
        variant: "error",
      });
    } else {
      enqueueSnackbar(error?.response?.data?.message || error?.message, {
        variant: "error",
      });
    }
  };

  const createFarmerMutationOptions = {
    onSuccess: handleCreateSuccess,
    onError: handleCreateError,
  };

  const {
    mutate: submitReg,
    // data: regFinalData,
    // isPending: mainRegistration
  } = useMutation({
    mutationFn: ContributionRegistration,
    ...createFarmerMutationOptions,
  });

  return (
    <>
      <form
        className="flex flex-col gap-[30px] "
        action=""
        onSubmit={formik.handleSubmit}
      >
        <div className="flex flex-col gap-[20px]">
          <div className="">
            <ParagraphComp
              className="text-black text-2xl"
              text="Undertaking Statements"
            />
          </div>
        </div>

        {undertakingCheckbox?.map((Field) => (
          <>
            <label className="flex items-center space-x-2">
              <InputCompo
                className="bg-[#004878] flex-shrink-0 text-white w-[20px] h-[20px]"
                required={true}
                type="checkbox"
                name={Field.name}
                checked={formik.values[Field.name]}
                onChange={formik.handleChange}
              />
              <span className="text-black text-wrap leading-9">
                {Field.text}
              </span>
            </label>
          </>
        ))}

        <div className="flex flex-col gap-[20px]">
          <div className="">
            <ParagraphComp
              className="text-black text-2xl"
              text="Contact Information"
            />
          </div>
        </div>

        {documents?.map((Fields) => (
          <>
            {Fields.type.toLowerCase() === "text" ||
            Fields.type.toLowerCase() === "number" ||
            Fields.type.toLowerCase() === "date" ? (
              <div className="w-[100%]">
                <CustomInput
                  required
                  {...formik.getFieldProps(Fields.question_no)}
                  name={Fields.question_no}
                  label={Fields.question}
                  onBlur={formik.handleBlur}
                />
                <ParagraphComp
                  className="text-[red] text-[10px] px-[8px] mt-1"
                  text={
                    formik.values?.[Fields.question_no] &&
                    formik.errors?.[Fields.question_no]
                  }
                />
                <ParagraphComp
                  className="mt-[8px] text-[#5A5A5A] text-sm px-[8px]"
                  text={Fields.description}
                />
              </div>
            ) : (
              ""
            )}
          </>
        ))}

        <div className="flex justify-end">
          <div className="flex gap-[20px] mb-20">
            {/* <ButtonComp text={'Save'}  className='px-[20px] rounded-full border-1 border-[#004878] text-[#004878] py-[10px]' /> */}
            <ButtonComp
              text={"submit"}
              type={"submit"}
              className="px-[20px] rounded-full bg-[#004878] text-[white] py-[10px]"
            />
          </div>
        </div>
      </form>
    </>
  );
};

export default UndertakingInformation;
